/**
 * Composant réutilisable pour la traduction automatique de recettes
 * Utilise Google Translate API (GRATUIT et ILLIMITÉ)
 * Support : Français, Khmer, Anglais
 */

import { useState, useEffect } from "react";
import { translateTextAPI } from "../../utils/translationAPI";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Hook personnalisé pour traduire automatiquement une recette complète
 */
export const useRecipeTranslation = (recipe) => {
  const { language } = useLanguage();
  const [translatedRecipe, setTranslatedRecipe] = useState(recipe);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (!recipe) return;

    const translateRecipe = async () => {
      setIsTranslating(true);

      try {
        // Traduire le nom de la recette
        const translatedName = recipe.name
          ? await translateTextAPI(recipe.name, language, "auto")
          : recipe.name;

        // Traduire la description
        const translatedDescription = recipe.description
          ? await translateTextAPI(recipe.description, language, "auto")
          : recipe.description;

        // Traduire les ingrédients (ligne par ligne)
        let translatedIngredients = recipe.ingredients;
        if (recipe.ingredients) {
          const ingredientLines = recipe.ingredients
            .split("\n")
            .filter((line) => line.trim());
          const translatedLines = await Promise.all(
            ingredientLines.map((line) =>
              translateTextAPI(line, language, "auto"),
            ),
          );
          translatedIngredients = translatedLines.join("\n");
        }

        // Traduire les instructions (ligne par ligne)
        let translatedInstructions = recipe.instructions;
        if (recipe.instructions) {
          const instructionLines = recipe.instructions
            .split("\n")
            .filter((line) => line.trim());
          const translatedLines = await Promise.all(
            instructionLines.map((line) =>
              translateTextAPI(line, language, "auto"),
            ),
          );
          translatedInstructions = translatedLines.join("\n");
        }

        // Créer la recette traduite
        setTranslatedRecipe({
          ...recipe,
          name: translatedName,
          description: translatedDescription,
          ingredients: translatedIngredients,
          instructions: translatedInstructions,
        });
      } catch (error) {
        console.error("Recipe translation error:", error);
        setTranslatedRecipe(recipe); // Fallback à la recette originale
      } finally {
        setIsTranslating(false);
      }
    };

    translateRecipe();
  }, [recipe, language]);

  return { translatedRecipe, isTranslating };
};

/**
 * Hook pour traduire une liste de recettes
 * Appliqué sur toutes les listes de cartes recettes (Recettes, Favoris, Communauté, Dashboard)
 */
export const useRecipesTranslation = (recipes) => {
  const { language } = useLanguage();
  const [translatedRecipes, setTranslatedRecipes] = useState(recipes ?? []);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const list = recipes ?? [];
    if (!list.length) {
      setTranslatedRecipes(list);
      return;
    }

    // Afficher les recettes tout de suite (évite écran vide pendant la traduction)
    setTranslatedRecipes(list);

    let cancelled = false;

    const translateRecipes = async () => {
      if (!cancelled) setIsTranslating(true);

      try {
        const translated = await Promise.all(
          list.map(async (recipe) => {
            const nameOrTitle = recipe.title ?? recipe.name ?? "";
            const translatedName = nameOrTitle
              ? await translateTextAPI(nameOrTitle, language, "auto")
              : nameOrTitle;

            const translatedDescription = recipe.description
              ? await translateTextAPI(recipe.description, language, "auto")
              : recipe.description;

            return {
              ...recipe,
              title: translatedName || recipe.title,
              name: translatedName || recipe.name,
              description: translatedDescription ?? recipe.description,
            };
          }),
        );

        if (!cancelled) setTranslatedRecipes(translated);
      } catch (error) {
        console.error("Recipes translation error:", error);
        if (!cancelled) setTranslatedRecipes(list);
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };

    translateRecipes();
    return () => {
      cancelled = true;
    };
  }, [recipes, language]);

  return { translatedRecipes, isTranslating };
};

/**
 * Composant d'indicateur de traduction
 */
export const TranslationIndicator = ({ isTranslating }) => {
  if (!isTranslating) return null;

  return (
    <div className="flex items-center gap-2 text-blue-600 text-sm">
      <div className="animate-spin">🌐</div>
      <span className="animate-pulse">Traduction en cours...</span>
    </div>
  );
};
